import axios from 'axios';
import { createAsyncThunk } from 'app/store/utils';
import { apiUrls } from 'app/api.config';
import { buildQueryUrl } from 'app/utils/utils';
import { CsvImportProgressPayload } from 'types/websocket.types';
import { ImportCSVEffectPayload } from 'types/import.types';
import { supportedLyricsVersion } from '../SongList/effects';
import { getImportRecordLink } from '../SongList/selectors';
import { addAlertAction } from '../Alerts/slice';

export const importCSVEffect = createAsyncThunk(
  'import/importCSV',
  async ({ csvText }: { csvTitle: string; csvText: string | ArrayBuffer }) => {
    if (!csvText) {
      throw new Error('CSV is missing');
    }

    try {
      const response = await axios.request({
        url: apiUrls.importCSV.post,
        method: 'POST',
        headers: {
          'Content-Type': 'text/csv',
        },
        withCredentials: true,
        data: csvText,
      });
      return response.data?.response?.csvImport
        ?.item as CsvImportProgressPayload;
    } catch (e: any) {
      if (e.response.data.error) {
        return e.response.data;
      }
      throw new Error(`Failed to import songs ${e}`);
    }
  }
);

export const songByTitleWildCardEffect = createAsyncThunk(
  'import/songByTitleWildCard',
  async ({ titleWildcard }: { titleWildcard: string }) => {
    if (!titleWildcard) {
      throw new Error('Wild Card Title is missing');
    }

    try {
      const response = await axios.get(apiUrls.songs, {
        params: { titleWildcard },
        withCredentials: true,
      });
      return response.data?.response?.song?.items?.[0] || null;
    } catch (e) {
      throw new Error(`Failed to get song by wild card title ${e}`);
    }
  }
);

export const importLyricsEffect = createAsyncThunk(
  'import/importLyrics',
  async (
    {
      songTitle,
      songGuid,
      text,
    }: {
      songTitle: string;
      songGuid?: string;
      text: string | ArrayBuffer;
    },
    { dispatch }
  ) => {
    if (!songGuid) {
      throw new Error('Song guid is missing');
    }

    if (!text) {
      return Promise.resolve(true);
    }

    try {
      const response = await axios.request({
        url: apiUrls.createLyrics.replace(':songGuid', songGuid),
        method: 'POST',
        withCredentials: true,
        data: {
          title: songTitle,
          body: text,
          supportedVersion: supportedLyricsVersion,
        },
      });
      return response.status === 201;
    } catch (e: any) {
      // Override already existing lyrics
      if (e.response.status === 409) {
        const lyricsResponse: any = await axios.request({
          url: apiUrls.createLyrics.replace(':songGuid', songGuid),
          withCredentials: true,
        });

        const { guid: lyricsGuid } =
          lyricsResponse?.data?.response?.lyricSheet?.items?.[0] || {};

        if (lyricsGuid) {
          const response = await axios.request({
            url: `${apiUrls.createLyrics.replace(
              ':songGuid',
              songGuid
            )}/${lyricsGuid}`,
            method: 'DELETE',
            withCredentials: true,
          });

          if (response.status === 204) {
            dispatch(
              importLyricsEffect({
                songTitle,
                text,
                songGuid,
              })
            );
          }
        }
      }
      throw new Error(`Failed to import lyrics ${e}`);
    }
  }
);

export const audioByTitleWildCardEffect = createAsyncThunk(
  'import/audioByTitleWildCard',
  async ({
    titleWildcard,
    songGuid,
  }: {
    titleWildcard: string;
    songGuid: string;
  }) => {
    if (!titleWildcard) {
      throw new Error('Wild Card Title is missing');
    }

    if (!songGuid) {
      throw new Error('Song Guid Title is missing');
    }

    const href = `${apiUrls.uploadAudio.replace(
      ':songGuid',
      songGuid
    )}?includeReadyForUpload=true`;

    try {
      const response = await axios.get(
        buildQueryUrl(href, {
          titleWildcard: [titleWildcard],
        }),
        {
          withCredentials: true,
        }
      );
      return response.data?.response?.audio?.items?.[0] || null;
    } catch (e) {
      throw new Error(`Failed to get audio by wild card title ${e}`);
    }
  }
);

export const getImportRecordEffect = createAsyncThunk(
  'import/getImportRecordEffect',
  async (_, { dispatch, getState }) => {
    const link = getImportRecordLink(getState());
    try {
      if (!link) {
        return null;
      }

      const response = await axios.get(link.href, {
        withCredentials: true,
      });
      return response.data.response.importRecord.item.record;
    } catch (e: any) {
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);

export const getCurrentImportEffect = createAsyncThunk(
  'import/getImportRecordEffect',
  async (_, { dispatch }) => {
    try {
      const response = await axios.get(apiUrls.importCSV.get, {
        withCredentials: true,
      });
      return response.data.response.import.item as ImportCSVEffectPayload;
    } catch (e: any) {
      if (e.response.data.error.statusCode === 404) {
        return null;
      }
      dispatch(addAlertAction({ message: 'Unexpected error occurred' }));
      throw e;
    }
  }
);
